/** Minimal line-based LCS diff for comparing document versions. */

export interface DiffLine {
  type: "same" | "add" | "del";
  text: string;
}

const MAX_LINES = 5000;

export function diffLines(oldText: string, newText: string): DiffLine[] {
  const a = oldText.split("\n").slice(0, MAX_LINES);
  const b = newText.split("\n").slice(0, MAX_LINES);

  // LCS length table (a.length+1 × b.length+1)
  const n = a.length;
  const m = b.length;
  const table: Uint32Array[] = [];
  for (let i = 0; i <= n; i++) table.push(new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      table[i][j] =
        a[i] === b[j]
          ? table[i + 1][j + 1] + 1
          : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }

  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ type: "same", text: a[i] });
      i++;
      j++;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      out.push({ type: "del", text: a[i] });
      i++;
    } else {
      out.push({ type: "add", text: b[j] });
      j++;
    }
  }
  while (i < n) out.push({ type: "del", text: a[i++] });
  while (j < m) out.push({ type: "add", text: b[j++] });
  return out;
}

/** Collapse runs of unchanged lines longer than 2*context+1 into a marker. */
export interface DiffBlock {
  type: "lines" | "skip";
  lines: DiffLine[];
  skipped?: number;
}

export function collapseContext(diff: DiffLine[], context = 3): DiffBlock[] {
  const blocks: DiffBlock[] = [];
  let run: DiffLine[] = [];

  const flushRun = (isLast: boolean) => {
    if (run.length === 0) return;
    const isFirst = blocks.length === 0;
    const keepStart = isFirst ? 0 : context;
    const keepEnd = isLast ? 0 : context;
    if (run.length > keepStart + keepEnd + 1) {
      if (keepStart > 0)
        blocks.push({ type: "lines", lines: run.slice(0, keepStart) });
      blocks.push({
        type: "skip",
        lines: [],
        skipped: run.length - keepStart - keepEnd,
      });
      if (keepEnd > 0)
        blocks.push({ type: "lines", lines: run.slice(run.length - keepEnd) });
    } else {
      blocks.push({ type: "lines", lines: run });
    }
    run = [];
  };

  for (const line of diff) {
    if (line.type === "same") {
      run.push(line);
    } else {
      flushRun(false);
      const last = blocks[blocks.length - 1];
      if (last && last.type === "lines") last.lines.push(line);
      else blocks.push({ type: "lines", lines: [line] });
    }
  }
  flushRun(true);
  return blocks;
}
